/**
 * The version tree, filtered by the search box above it
 * (components/customDragComponent.js).
 *
 * What survives a search:
 *
 *   - a FOLDER found by its name, with everything inside it however deep.
 *     Looking for the folder is looking for what is in it, not for the one
 *     version that happens to repeat the folder's name.
 *   - a VERSION found by its name.
 *   - the folders ABOVE either of those, so the match's place in the tree is
 *     still readable. Nothing else: a folder that neither matches nor holds a
 *     surviving child is dropped, which is what takes the match's siblings off
 *     the list while the way down to it stays. Searching "7" in 1 › 2 › 5 › 6 ›
 *     7 leaves 1, 2, 5, 6, 7 and drops 2's sibling folders.
 *
 * The caller opens the whole tree while a search is running
 * (customDragComponent's `initialOpen`), because a match left inside a
 * collapsed ancestor is no result at all.
 *
 * A folder is a node whose `data.fileType` is NOT "text"; "text" is a version.
 * The naming is historical and reads backwards — a new folder is also written
 * with `droppable: true` (dialogFolderInsert) while a version is not, but older
 * stored hierarchies carry only the fileType, so that is what this goes by.
 * Either way it is worth having one place that knows which is which.
 */

/** @returns {boolean} true for a folder node, false for a version. */
function isFolder(node) {
  return node.data?.fileType !== "text";
}

/**
 * @param {Array} nodes  the flat @minoru/react-dnd-treeview node list
 * @param {string} term  what was typed; an empty one returns `nodes` unchanged
 * @returns {Array} the surviving nodes, re-parented where their parent did not
 *   survive. The input is never mutated.
 */
export function filterVersionTree(nodes, term) {
  const needle = String(term || "").trim().toLowerCase();
  const all = Array.isArray(nodes) ? nodes : [];
  if (!needle) return all;

  const matches = (node) => String(node.text || "").toLowerCase().includes(needle);

  const childrenOf = new Map();
  for (const node of all) {
    const parentId = String(node.parent);
    if (!childrenOf.has(parentId)) childrenOf.set(parentId, []);
    childrenOf.get(parentId).push(node);
  }

  const byId = new Map(all.map((node) => [String(node.id), node]));

  const kept = new Set();
  const keepWithEverythingUnder = (node) => {
    const id = String(node.id);
    if (kept.has(id)) return;
    kept.add(id);
    for (const child of childrenOf.get(id) || []) keepWithEverythingUnder(child);
  };
  const keepAncestors = (node) => {
    let parent = byId.get(String(node.parent));
    while (parent && !kept.has(String(parent.id))) {
      kept.add(String(parent.id));
      parent = byId.get(String(parent.parent));
    }
  };

  for (const node of all) {
    if (!matches(node)) continue;
    if (isFolder(node)) keepWithEverythingUnder(node);
    else kept.add(String(node.id));
    keepAncestors(node);
  }

  // Parents are kept with their children, so nothing dangles and no node has
  // to be re-parented — the surviving nodes are the original tree with the
  // branches that hold no match cut off.
  return all.filter((node) => kept.has(String(node.id)));
}
